import {
  CircularProgress,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import {
  ProducerJobByIdDocument,
  ProducerJobByIdQuery,
  useCutsheetQuestionnaireResponseByIdQuery,
  useProcessorSchedulingCutsheetsPartialUpdateMutation,
} from 'lib/graphql';
import { userState } from 'state';
import { isBuyerOnly, isProcessor } from 'lib/userHelpers';

import {
  useCatalogState,
  useQuestionnaireState,
} from 'features/cutsheets/v2/hooks';
import { QuestionnaireWizard } from 'features/cutsheets/v2/components';
import type {
  InitialResponseSeed,
  ResponseTrimSelection,
} from 'features/cutsheets/v2/components/questionnaire-wizard/types';

import { getQuestionnaireId } from './split-slot/utils';

type Job = NonNullable<ProducerJobByIdQuery['processorSchedulingById']>;
type AnimalHead = NonNullable<Job['animalHeads']>[number];
type CutsheetInfo = NonNullable<
  NonNullable<NonNullable<AnimalHead>['cutsheetInformation']>[number]
>;

interface ProducerQuestionnaireEditWizardProps {
  open: boolean;
  onClose: () => void;
  job: Job;
  cutsheetInfo: CutsheetInfo;
}

/**
 * Opens the QuestionnaireWizard seeded with the slot's existing response so
 * the user can adjust answers and save as a new copy. On save the slot is
 * re-pointed at the newly created response.
 *
 * Data is pulled entirely off the response itself (questionnaire + capability
 * ids) so this works even when the parent slot is missing the `questionnaire`
 * field or the job has no vendor resolved yet.
 */
export function ProducerQuestionnaireEditWizard({
  open,
  onClose,
  job,
  cutsheetInfo,
}: ProducerQuestionnaireEditWizardProps) {
  const user = useRecoilValue(userState);

  const contactUserId = cutsheetInfo.contactUser?._id;
  const isSelf = contactUserId === user?._id;

  const responseId = (
    cutsheetInfo.questionnaireResponse as { _id?: string } | null | undefined
  )?._id;

  const {
    data: responseData,
    loading: responseLoading,
    error: responseError,
  } = useCutsheetQuestionnaireResponseByIdQuery({
    variables: { _id: responseId ?? '' },
    skip: !open || !responseId,
    fetchPolicy: 'cache-and-network',
  });

  const response = responseData?.cutsheetQuestionnaireResponseById;

  // The API populates `questionnaire` and `capabilitySet`, so they arrive as
  // objects at runtime — extract the scalar id in a shape-tolerant way.
  const questionnaireId =
    getQuestionnaireId(response?.questionnaire) ??
    getQuestionnaireId(cutsheetInfo.questionnaire) ??
    undefined;
  const capabilityId = getQuestionnaireId(response?.capabilitySet) ?? undefined;

  const { questionnaire, loading: questionnaireLoading } =
    useQuestionnaireState({
      questionnaireId,
      capabilityId,
    });

  const { catalog, loading: catalogLoading } = useCatalogState({
    vendorId: (job.vendor?._id as string | undefined) ?? '',
    capabilityId: capabilityId ?? questionnaire?.capabilityId ?? '',
  });

  const initialResponse = useMemo<InitialResponseSeed | undefined>(() => {
    if (!response) return undefined;
    return {
      name: response.name ?? undefined,
      selectedCuts: (response.selectedCuts ?? [])
        .filter((c): c is NonNullable<typeof c> => Boolean(c))
        .map((c) => ({
          optionId: c.optionId,
          specifications: c.specifications
            ?.filter((s): s is NonNullable<typeof s> => Boolean(s))
            .map((s) => ({
              specification: s.specification,
              type: s.type as 'text' | 'number' | 'select' | 'multiselect',
              value: s.value as string | number | string[],
            })),
        })),
      trimSelections: response.trimSelections
        ? {
            type: response.trimSelections.type as
              | 'ranked'
              | 'selection_by_percentage',
            trim: (response.trimSelections.trim ?? [])
              .filter((t): t is NonNullable<typeof t> => Boolean(t))
              .map((t) => {
                const specifications = t.specifications
                  ?.filter((s): s is NonNullable<typeof s> => Boolean(s))
                  .map((s) => ({
                    specification: s.specification,
                    type: s.type as
                      | 'text'
                      | 'number'
                      | 'select'
                      | 'multiselect',
                    value: s.value as string | number | string[],
                  }));
                if (response.trimSelections!.type === 'ranked') {
                  return {
                    optionId: t.optionId,
                    rank: t.rank ?? 0,
                    quantity: t.quantity ?? 0,
                    ...(specifications ? { specifications } : {}),
                  } satisfies ResponseTrimSelection;
                }
                return {
                  optionId: t.optionId,
                  percentage: t.percentage ?? 0,
                  ...(specifications ? { specifications } : {}),
                } satisfies ResponseTrimSelection;
              }),
          }
        : undefined,
      specifications: (response.specifications ?? [])
        .filter((s): s is NonNullable<typeof s> => Boolean(s))
        .map((s) => ({
          specification: s.specification,
          type: s.type as 'text' | 'number' | 'select' | 'multiselect',
          value: s.value as string | number | string[],
        })),
    };
  }, [response]);

  const refetchJob = useMemo(
    () => ({ query: ProducerJobByIdDocument, variables: { id: job._id } }),
    [job._id],
  );

  const [updateCutsheets] =
    useProcessorSchedulingCutsheetsPartialUpdateMutation();

  const handleSave = useCallback(
    async (newResponseId: string) => {
      await updateCutsheets({
        variables: {
          processorSchedulingId: job._id,
          record: [
            { _id: cutsheetInfo._id, questionnaireResponse: newResponseId },
          ],
        },
        refetchQueries: [refetchJob],
      });
      onClose();
    },
    [updateCutsheets, job._id, cutsheetInfo._id, refetchJob, onClose],
  );

  if (!open) return null;

  const isLoading = responseLoading || questionnaireLoading || catalogLoading;
  const ready = !!questionnaire && !!catalog && !!initialResponse;

  if (!ready) {
    return (
      <Dialog open fullScreen onClose={onClose}>
        <DialogContent>
          <Stack
            alignItems="center"
            justifyContent="center"
            gap={2}
            sx={{ height: '100%' }}
          >
            {isLoading ? (
              <>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Loading cutsheet response…
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  Unable to load cutsheet response.
                </Typography>
                {responseError && (
                  <Typography variant="caption" color="error">
                    {responseError.message}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {!responseId
                    ? 'No response attached to this slot.'
                    : !response
                      ? 'Response could not be fetched.'
                      : !questionnaireId
                        ? 'Response is missing a cutsheet reference.'
                        : !questionnaire
                          ? 'Questionnaire could not be fetched.'
                          : !capabilityId && !questionnaire.capabilityId
                            ? 'Response is missing a capability reference.'
                            : !catalog
                              ? 'Catalog could not be loaded.'
                              : 'Preparing wizard…'}
                </Typography>
              </>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <QuestionnaireWizard
      open={open}
      onClose={onClose}
      onSave={handleSave}
      questionnaire={questionnaire}
      catalog={catalog}
      isProcessor={user ? isProcessor(user) : false}
      producerId={job.requestedBy?._id ?? undefined}
      buyerId={!isSelf ? (contactUserId ?? undefined) : undefined}
      cutsheetType={user && isBuyerOnly(user) ? 'user' : undefined}
      initialResponse={initialResponse}
    />
  );
}
